import type { Transaction } from '@solana/web3.js';
import type {
  CashoutPaymentInstructions,
  MarketIntentPaymentInstructions,
  SolanaPaymentContext,
  SpinnerPaymentInstructions,
} from '../api/types';
import { getPhantomProvider } from '../auth/wallet';
import {
  buildCashoutPaymentTransaction,
  buildMarketplacePaymentTransaction,
  buildSpinnerPaymentTransaction,
} from './payment';

export interface SolanaWalletPayments {
  payCashout(payment: CashoutPaymentInstructions, payerWallet: string, context: SolanaPaymentContext): Promise<string>;
  payMarketplace(payment: MarketIntentPaymentInstructions, payerWallet: string, context: SolanaPaymentContext): Promise<string>;
  paySpinner(payment: SpinnerPaymentInstructions, payerWallet: string, context: SolanaPaymentContext): Promise<string>;
}

export const browserSolanaPayments: SolanaWalletPayments = {
  async payCashout(payment, payerWallet, context) {
    return signAndSend(buildCashoutPaymentTransaction({ payerWallet, payment, context }), payerWallet);
  },
  async payMarketplace(payment, payerWallet, context) {
    return signAndSend(buildMarketplacePaymentTransaction({ payerWallet, payment, context }), payerWallet);
  },
  async paySpinner(payment, payerWallet, context) {
    return signAndSend(buildSpinnerPaymentTransaction({ payerWallet, payment, context }), payerWallet);
  },
};

interface PaymentProvider {
  publicKey?: { toString(): string } | null;
  signAndSendTransaction?(transaction: Transaction): Promise<{ signature?: string } | string>;
}

async function signAndSend(transaction: Transaction, payerWallet: string): Promise<string> {
  const provider = getPhantomProvider() as PaymentProvider | null;
  if (!provider) throw new Error('Phantom wallet not found. Install or unlock Phantom, then try again.');
  const connected = provider.publicKey?.toString();
  if (connected && connected !== payerWallet) {
    throw new Error('Connected Phantom wallet does not match the wallet bound to this account.');
  }
  if (!provider.signAndSendTransaction) {
    throw new Error('This wallet does not support direct sign-and-send payments. Use the manual signature fallback.');
  }
  const result = await provider.signAndSendTransaction(transaction);
  const signature = typeof result === 'string' ? result : result.signature;
  if (!signature) throw new Error('Wallet did not return a transaction signature.');
  return signature;
}
